import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  GET_ACCOUNT_TYPE_SUMMARY,
  GET_BALANCE_SHEET_REPORT,
  GET_CASH_FLOW_REPORT,
  GET_BALANCE_SUMMARY_REPORT,
  GET_GENERAL_LEDGER_REPORT,
  GET_JOURNAL_REPORT,
  GET_PROFIT_LOSS_REPORT,
  GET_SALES_BY_CUSTOMER_REPOTT,
  GET_SALES_BY_ITEM_REPORT,
  VENDOR_BALANCE_SUMMARY_REPORT,
  TRIAL_BALANCE_REPORT,
  GET_INVOICE_AGING_REPORT,
  GET_VENDOR_INVOICE_AGING_REPORT,
  GET_COST_REPORT,
  GET_ACCOUNT_LEDGER_REPORT,
  GET_VAT_REPORT,
  GET_DAY_BOOK_REPORT,
  MOVEMENT_TRIAL_BALANCE_REPORT,
} from '../graphql/querries/reports.query';
import {
  EXPORT_ACCOUNT_REPORT,
  EXPORT_ACCOUNT_TYPE_SUMMARY_TO_CSV,
  EXPORT_BALANCE_SHEET_TO_CSV,
  EXPORT_BILL_INVOICE_AGING_REPORT,
  EXPORT_CASH_FLOW_TO_CSV,
  EXPORT_CUSTOMER_BALANCE_SUMMARY_TO_CSV,
  EXPORT_GENERAL_LEDGER_TO_CSV,
  EXPORT_INVOICE_AGING_REPORT,
  EXPORT_JOURNALS_REPORT_TO_CSV,
  EXPORT_PAYMENT_MADE_TO_CSV,
  EXPORT_PAYMENT_RECEIVED_TO_CSV,
  EXPORT_PETTY_CASH_SUMMARY_TO_CSV,
  EXPORT_PROFIT_LOSS_TO_CSV,
  EXPORT_SALES_BY_CUSTOMER_TO_CSV,
  EXPORT_SALES_BY_ITEM_TO_CSV,
  EXPORT_VENDOR_BALANCE_SUMMARY_TO_CSV,
} from '../graphql/mutations/reports.mutation';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  constructor(private apollo: Apollo) {}

  //export sales by customer report
  exportSalesByCustomerToCSV(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate<any>({
        mutation: EXPORT_SALES_BY_CUSTOMER_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportSalesByCustomerToCSV));
  }

  //export sales by item report
  exportSalesByItemToCSV(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('project')
      .mutate<any>({
        mutation: EXPORT_SALES_BY_ITEM_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportSalesByItemToCSV));
  }

  //export customer balance summary
  exportCustomerBalanceSummaryToCSV(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('project')
      .mutate<any>({
        mutation: EXPORT_CUSTOMER_BALANCE_SUMMARY_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportCustomerBalanceSummaryToCSV));
  }

  //export profit loss
  exportProfitLossToCSV(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_PROFIT_LOSS_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportProfitLossToCSV));
  }

  //export payment received
  exportPaymentReceivedToCSV(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_PAYMENT_RECEIVED_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportPaymentReceivedToCSV));
  }

  //export payment made
  exportPaymentMadeToCSV(
    startDate: string,
    endDate: string,
    fields: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_PAYMENT_MADE_TO_CSV,
        variables: {
          startDate,
          endDate,
          fields,
        },
      })
      .pipe(map((result) => result.data?.exportPaymentReceivedToCSV));
  }

  //export balance sheet
  exportBalanceSheetToCSV(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_BALANCE_SHEET_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportBalanceSheetToCSV));
  }

  //export cash flow
  exportCashFlowToCSV(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_CASH_FLOW_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportCashFlowToCSV));
  }

  //export invoice aging report
  exportInvoiceAgingReport(
    daysFilter: string,
    partnerId: number
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_INVOICE_AGING_REPORT,
        variables: {
          daysFilter,
          partnerId,
        },
      })
      .pipe(map((response) => response.data?.exportInvoiceAgingReport));
  }

  //exportr bill invoice aging report
  exportBillAgingReport(daysFilter: string, vendorId: number): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_BILL_INVOICE_AGING_REPORT,
        variables: {
          daysFilter,
          vendorId,
        },
      })
      .pipe(map((response) => response?.data?.exportBillAgingReport));
  }

  //export petty cash summary
  exportPettyCashSummary(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_PETTY_CASH_SUMMARY_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportPettyCashSummary));
  }

  //export account report
  exportAccountsReport(
    accountId: number,
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_ACCOUNT_REPORT,
        variables: {
          accountId,
          startDate,
          endDate,
        },
      })
      .pipe(map((response) => response.data?.exportAccountReport));
  }

  //export vendor balance
  exportVendorBalanceSummary(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_VENDOR_BALANCE_SUMMARY_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportVendorBalanceSummary));
  }

  //export account type summary
  exportAccountTypesSummaryToCSV(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_ACCOUNT_TYPE_SUMMARY_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportAccountTypesSummaryToCSV));
  }

  //export general ledger
  exportJournalsLedgerToCSV(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_GENERAL_LEDGER_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportJournalsLedgerToCSV));
  }

  //export Journal report
  exportJournalsReportToCSV(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate<any>({
        mutation: EXPORT_JOURNALS_REPORT_TO_CSV,
        variables: {
          startDate,
          endDate,
        },
      })
      .pipe(map((result) => result.data?.exportJournalsReportToCSV));
  }

  fetchProfitLossReport(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_PROFIT_LOSS_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.profitLossReport || [])
      );
  }

  fetchCashFlowReport(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_CASH_FLOW_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.cashFlowStatement || [])
      );
  }

  fetchBalanceSheet(startDate: any, endDate: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_BALANCE_SHEET_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.balanceSheetReport || [])
      );
  }

  fetchSalesByItem(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_SALES_BY_ITEM_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data.salesByItem || []));
  }

  fetchsalesByCustomer(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('project')
      .watchQuery<any>({
        query: GET_SALES_BY_CUSTOMER_REPOTT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.salesByCustomer || [])
      );
  }

  fetchAccountTypeSummaryReport(
    startDate: string,
    endDate: string,
    branchId?: number
  ): Observable<any> {
    const variables: any = {
      startDate,
      endDate,
    };

    if (branchId !== undefined && branchId !== null) {
      variables.branchId = branchId;
    }
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_ACCOUNT_TYPE_SUMMARY,
        variables,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.getAccountTypesSummary || [])
      );
  }

  fetchJournalReport(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_JOURNAL_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data.journalReport || []));
  }

  fetchGeneralLedgerReport(filters: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_GENERAL_LEDGER_REPORT,
        variables: { filters },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.fetchGeneralLedgerReport || [])
      );
  }

  fetchBalanceSummary(data: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_BALANCE_SUMMARY_REPORT,
        variables: { data },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response.data.balanceSummary || []));
  }

  generalLedgerReport(
    startDate: any,
    endDate: any,
    accountId?: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_ACCOUNT_LEDGER_REPORT,
        variables: { startDate, endDate, accountId },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response?.data?.generalLedgerReport || [])
      );
  }

  getCostReport(
    projectId: number,
    startDate: string,
    endDate: string
  ): Observable<any> {
    console.log('projectId', projectId);
    console.log('startDate', startDate);
    console.log('endDate', endDate);
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_COST_REPORT,
        variables: { projectId, startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response?.data?.getProjectReport || [])
      );
  }

  fetchAgingReportInvoice(filters: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_INVOICE_AGING_REPORT,
        variables: { filters },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.fetchAgingReportInvoice || [])
      );
  }

  fetchAgingReportVendorInvoice(filters: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_VENDOR_INVOICE_AGING_REPORT,
        variables: { filters },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.fetchAgingReportVendorInvoice || [])
      );
  }


  fetchVendorBalanceReport(
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: VENDOR_BALANCE_SUMMARY_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.getVendorBalanceSummary || [])
      );
  }

  trialBalanceReport(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: TRIAL_BALANCE_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.trialBalanceReport || [])
      );
  }

  vatReport(startDate: any, endDate: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_VAT_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((response) => response?.data?.vatReport || []));
  }

  fetchDayBookReport(date: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_DAY_BOOK_REPORT,
        variables: { date },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response?.data?.dayBookReport || [])
      );
  }

  fetchMovementTrialBalanceReport(startDate: string, endDate: string): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: MOVEMENT_TRIAL_BALANCE_REPORT,
        variables: { startDate, endDate },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((response) => response.data.movementTrialBalanceReport || [])
      );
  }
}
